import React from "react";
import PageHero from "../components/PageHero";
import GHLBooking from "../components/GHLBooking";
import ZipChecker from "../components/ZipChecker";
import { Phone, Mail, MapPin, Clock, Star, Navigation } from "lucide-react";
import { company } from "../mock";
import useSEO from "../hooks/useSEO";

const items = [
  { icon: Phone, label: "Call Us", value: company.phone, href: company.phoneHref },
  { icon: Mail, label: "Email Us", value: company.email, href: `mailto:${company.email}` },
  { icon: MapPin, label: "Service Area", value: "N. Virginia · D.C. · Maryland" },
  { icon: Clock, label: "Hours", value: company.hours },
];

const Contact = () => {
  useSEO({
    title: "Contact Fab Build Pro | Roofer in Vienna, VA | 703-766-6599",
    description: "Contact Fab Build Pro at 1934 Old Gallows Rd STE 350, Vienna, VA 22182. Call 703-766-6599 or book a free roof inspection online. Open 24/7.",
    crumbs: [{ name: "Contact", path: "/contact" }],
  });
  return (
    <>
      <PageHero
        title="Contact Us"
        subtitle="Get your free, no-obligation estimate today. Our team is ready to help protect your home."
      />

      <section className="bg-brand-cream py-16">
        <div className="max-w-7xl mx-auto px-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {items.map((it) => {
            const Inner = (
              <>
                <div className="w-12 h-12 rounded-full bg-brand-orange flex items-center justify-center mb-4">
                  <it.icon className="w-6 h-6 text-white" />
                </div>
                <p className="text-sm text-slate-500">{it.label}</p>
                <p className="font-display font-bold text-brand-navy mt-0.5">
                  {it.value}
                </p>
              </>
            );
            return it.href ? (
              <a
                key={it.label}
                href={it.href}
                className="bg-white rounded-2xl p-6 border border-black/5 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all"
              >
                {Inner}
              </a>
            ) : (
              <div
                key={it.label}
                className="bg-white rounded-2xl p-6 border border-black/5 shadow-sm"
              >
                {Inner}
              </div>
            );
          })}
        </div>

        <div className="max-w-md mx-auto px-6 mt-12">
          <div className="bg-brand-navy rounded-3xl p-8">
            <ZipChecker variant="dark" />
          </div>
        </div>
      </section>

      <GHLBooking />

      <section className="bg-white py-16" id="visit-us" data-testid="google-map-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-3 gap-8 items-stretch">
            <div className="lg:col-span-1 flex flex-col justify-center">
              <p className="text-brand-orange font-semibold tracking-[0.18em] text-sm uppercase mb-3">
                Visit Us
              </p>
              <h2 className="font-display font-extrabold text-brand-navy text-3xl md:text-4xl leading-tight mb-4">
                Find Us on Google
              </h2>
              <div className="flex items-center gap-2 mb-4">
                <div className="flex">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      className="w-5 h-5 text-amber-400 fill-amber-400"
                    />
                  ))}
                </div>
                <span className="font-display font-bold text-brand-navy">
                  {company.rating}
                </span>
                <span className="text-slate-500 text-sm">
                  ({company.reviewCount}+ Google reviews)
                </span>
              </div>
              <p className="text-slate-600 leading-relaxed mb-6" data-testid="contact-nap">
                <strong className="text-brand-navy">Fab Build Pro</strong> — roofing &amp; general contractor
                <br />
                {company.address}
                <br />
                <a href={company.phoneHref} className="hover:text-brand-orange">{company.phone}</a> ·{" "}
                <a href={`mailto:${company.email}`} className="hover:text-brand-orange">{company.email}</a>
                <br />
                {company.hours}. Serving Northern Virginia, Washington, D.C. and Maryland.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <a
                  href={company.googleReviewsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="review-on-google-btn"
                  className="inline-flex items-center justify-center gap-2 bg-brand-orange hover:bg-brand-orangedark text-white font-semibold px-5 py-3 rounded-full shadow-md hover:shadow-lg transition-all hover:-translate-y-0.5"
                >
                  <Star className="w-5 h-5" />
                  Review us on Google
                </a>
                <a
                  href={company.directionsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="get-directions-btn"
                  className="inline-flex items-center justify-center gap-2 bg-white text-brand-navy border border-brand-navy/15 font-semibold px-5 py-3 rounded-full shadow-sm hover:shadow-md transition-all hover:-translate-y-0.5"
                >
                  <Navigation className="w-5 h-5" />
                  Get Directions
                </a>
              </div>
            </div>

            <div className="lg:col-span-2">
              <div className="rounded-3xl overflow-hidden shadow-xl border border-black/5 h-[380px] lg:h-full min-h-[380px]">
                <iframe
                  title="Fab Build Pro on Google Maps"
                  data-testid="google-map-iframe"
                  src={company.mapsEmbedUrl}
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Contact;
